import React from 'react';
import { Controller } from 'react-hook-form';
import Select, { components, StylesConfig } from 'react-select';
import { useTranslation } from 'react-i18next';
import ToolTip from 'app/routes/Scheduling/ToolTip';
import CheckMark from '../../../../public/images/checkmark-green.png';
import styles from './StripeAccountSelector.module.scss';

interface CoachOption {
  id: string;
  value: string;
  label: string;
  profilePicture?: string;
  allowsPayments?: boolean;
}

interface PropsDef {
  control: any;
  stripeCoachOptions: CoachOption[];
  stripeEnabledCoaches: CoachOption[];
  collectOnline: boolean;
  canEditPaymentCollector: boolean;
  durationCustomStyles: StylesConfig;
  // Free clinics collect no payment, but the creator still picks the coach the
  // registrations are attributed to (they appear in that coach's Reports).
  isFree?: boolean;
  // Direction the info tooltip opens, so it doesn't clip against the container
  // edge. Default 'right' (bubble extends left) suits a right-column layout;
  // pass 'left' when the field sits in a left column (bubble extends right).
  tooltipOrientation?: 'left' | 'right' | 'center';
}

const DropdownIndicator = (props: any) => {
  const open = props.selectProps.menuIsOpen;
  return (
    <components.DropdownIndicator {...props}>
      <svg
        width="18"
        height="18"
        viewBox="0 0 20 20"
        style={{ transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        aria-hidden="true"
      >
        <path d="M5 7l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </components.DropdownIndicator>
  );
};

export default function StripeAccountSelector(props: PropsDef) {
  const { control, stripeCoachOptions, stripeEnabledCoaches, collectOnline, canEditPaymentCollector, durationCustomStyles, isFree, tooltipOrientation = 'right' } = props;
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.selectorHeader}>
        <span className={styles.gray_text} style={{ margin: '6px 0' }}>
          {isFree ? t("Attribute Registrations to") : t("Select Collecting Coach")}
        </span>
        <span className={styles.inlineTip}>
          <ToolTip
            orientation={tooltipOrientation}
            placement="bottom"
            fontSize="18px"
            title={isFree ? t("Attribute Registrations to") : collectOnline ? t("Stripe Account") : t("Collecting Coach")}
            text={
              isFree
                ? t("Registrations for this free clinic will appear in the selected coach's Reports.")
                : collectOnline
                  ? t("Select the coach who will receive payments from this Program.")
                  : t("Select the coach responsible for collecting offline payments.")
            }
            iconColor="gray"
          />
        </span>
      </div>

      <Controller
        name="stripe_account_id"
        control={control}
        rules={{
          validate: (value) => {
            if (!value && collectOnline) {
              return t("This field is required. Online payments require a coach with a connected Stripe account.");
            }
            if (!value) {
              return t("This field is required");
            }
            if (!collectOnline) {
              return true;
            }

            const selectedCoach = stripeCoachOptions.find((c) => c.id === value);

            // Check if the selected coach is still in the program
            if (!selectedCoach) {
              return t("The selected coach is no longer in this program. Please select a different coach.");
            }

            if (!selectedCoach.allowsPayments) {
              return t("Please select a coach with a connected Stripe account or disable online payments.");
            }
            return true;
          },
        }}
        render={({ field, fieldState }) => {
          const customSingleValue = ({ data }: any) => (
            <div style={{ display: "flex", alignItems: "center" }}>
              {data?.profilePicture && (
                <img
                  src={data.profilePicture}
                  alt={data.label}
                  style={{ width: 24, height: 24, borderRadius: "50%", marginRight: 8 }}
                />
              )}
              {data.label}
            </div>
          );

          const customOption = (props: any) => {
            const { data, innerRef, innerProps, isSelected } = props;
            return (
              <div
                ref={innerRef}
                {...innerProps}
                style={{ padding: 10, display: "flex", alignItems: "center", cursor: "pointer" }}
              >
                {data?.profilePicture && (
                  <img
                    src={data.profilePicture}
                    alt={data.label}
                    style={{ width: 24, height: 24, borderRadius: "50%", marginRight: 8 }}
                  />
                )}
                {data.label}
                {isSelected && <img className={styles.checkboxImagePayment} src={CheckMark} alt="Checkmark" />}
              </div>
            );
          };

          const value =
            stripeCoachOptions.find((o) => o.id === field.value) || null;

          return (
            <div className={styles.selectStripeAccount}>
              <Select
                {...field}
                value={value}
                onChange={(opt: any) => field.onChange(opt?.id ?? null)}
                options={stripeCoachOptions}
                isClearable
                isDisabled={!canEditPaymentCollector}
                placeholder={t("Select")}
                styles={durationCustomStyles}
                classNamePrefix="stripeSelect"
                components={{
                  SingleValue: customSingleValue,
                  Option: customOption,
                  IndicatorSeparator: () => null,
                  DropdownIndicator,
                }}
              />
              {fieldState.error && (
                <span className={styles.error}>{fieldState.error.message}</span>
              )}
              {collectOnline && stripeEnabledCoaches.length === 0 && (
                <div className={styles.helperText}>
                  {t("No selected coaches have Stripe enabled.")}
                </div>
              )}
            </div>
          );
        }}
      />
    </>
  );
}
